import {
  addDays,
  differenceInCalendarDays,
  isDate,
  isSameDay,
  subDays,
} from 'date-fns'
import { actionTypes, change, FormAction, getFormValues } from 'redux-form'
import { all, put, select, take, takeLatest } from 'redux-saga/effects'
import calendarIsReady, {
  calendarIsLoading,
} from '../../actions/bookingCalendar/calendar/calendarIsReady'
import { changeDateManual } from '../../actions/bookingCalendar/date/changeDateRange'
import { IApplyFiltersDrawer } from '../../actions/bookingCalendar/filtering/applyFiltersDrawer'
import {
  filterByDate,
  IFilterCalendarByDate,
} from '../../actions/bookingCalendar/filtering/filterByDate'
import { filterByPlace } from '../../actions/bookingCalendar/filtering/filterByPlace'
import { IFilterRoomsValues } from '../../containers/Form/FilterRooms/withReduxForm'
import { localStorage } from '../../database'
import {
  CalendarActions,
  CalendarFiltersActions,
} from '../../reducers/bookingCalendar/actionNames'
import { AuthorizationActions } from '../../reducers/userAccount/actionNames'

function* applyFiltering(action: IApplyFiltersDrawer) {
  yield put(calendarIsLoading())
  yield put(
    filterByDate({
      days: action.filters.daysCount,
      from: action.filters.dateFrom,
      to: action.filters.dateTo,
    }),
  )
  yield put(
    filterByPlace({
      name: action.filters.name,
      persons: action.filters.numPersons,
    }),
  )

  const { UserDriver: Driver, Names } = localStorage
  Driver.setItem(Names.RoomsFiltering.Options, JSON.stringify(action.filters))

  yield put(calendarIsReady())
}

function* clearFiltering() {
  yield put(calendarIsLoading())
  yield put(
    filterByDate({
      days: 1,
      from: undefined,
      to: undefined,
    }),
  )
  yield put(
    filterByPlace({
      name: '',
      persons: 0,
    }),
  )

  const { UserDriver: Driver, Names } = localStorage
  Driver.setItem(Names.RoomsFiltering.Options, JSON.stringify({ filters: {} }))

  yield put(calendarIsReady())
}

function* centerScreen({ from, to }: IFilterCalendarByDate) {
  if (!isDate(from) || !isDate(to)) {
    return
  }

  // const [scrollableFrom, scrollableTo, extendLeftBy, extendRightBy] = yield all(
  //   [
  //     select(getScrollableFromDate),
  //     select(getScrollableToDate),
  //     select(getScrollableExtendLeft),
  //     select(getScrollableExtendRight),
  //   ],
  // )
  //
  // const newFrom =
  //   from < addMonths(scrollableFrom, -extendLeftBy)
  //     ? addMonths(from, -extendLeftBy)
  //     : scrollableFrom
  // const newTo =
  //   to > addMonths(scrollableTo, extendRightBy)
  //     ? addMonths(to, extendRightBy)
  //     : scrollableTo
  //
  // if (+newFrom !== +scrollableFrom || +newTo !== +scrollableTo) {
  //   yield put(changeDateScrollable(newFrom, newTo))
  //
  //   yield take(DateActions.ScrollableDateRangeChanged)
  // }

  yield take(CalendarActions.IsReady)
  yield put(changeDateManual(from))
}

function* updatedDaysCounter(action: IUpdateDaysFieldAction) {
  if (action.meta.form !== 'filterRooms' || action.meta.field !== 'daysCount') {
    return
  }
  const { dateFrom }: IFilterRoomsValues | undefined = yield select(
    getFormValues('filterRooms'),
  )

  if (!isDate(dateFrom)) {
    const startDate = new Date()
    const finishDate = addDays(startDate, action.payload)

    yield all([
      put(change(action.meta.form, 'dateFrom', startDate)),
      put(change(action.meta.form, 'dateTo', finishDate)),
    ])
  }

  if (isDate(dateFrom)) {
    const finishDate = addDays(dateFrom, action.payload)

    yield all([put(change(action.meta.form, 'dateTo', finishDate))])
  }
}

function* updatedDateFrom(action: IUpdateDateToAction) {
  if (action.meta.form !== 'filterRooms' || action.meta.field !== 'dateFrom') {
    return
  }

  const { dateTo, daysCount }: IFilterRoomsValues | undefined = yield select(
    getFormValues('filterRooms'),
  )

  if (
    !isDate(dateTo) ||
    isSameDay(dateTo, action.payload) ||
    differenceInCalendarDays(dateTo, action.payload) <= 0
  ) {
    yield put(change(action.meta.form, 'daysCount', 1))
  } else {
    const diffInDays = differenceInCalendarDays(dateTo, action.payload)
    if (daysCount !== diffInDays) {
      yield put(change(action.meta.form, 'daysCount', diffInDays))
    }
  }
}

function* updatedDateTo(action: IUpdateDateToAction) {
  if (action.meta.form !== 'filterRooms' || action.meta.field !== 'dateTo') {
    return
  }

  const { dateFrom, daysCount }: IFilterRoomsValues | undefined = yield select(
    getFormValues('filterRooms'),
  )

  if (isDate(dateFrom)) {
    if (dateFrom > action.payload) {
      yield put(
        change(action.meta.form, 'dateFrom', subDays(action.payload, 1)),
      )
    } else {
      const diffInDays = differenceInCalendarDays(action.payload, dateFrom)
      if (daysCount !== diffInDays && diffInDays >= 0) {
        yield put(change(action.meta.form, 'daysCount', diffInDays))
      }
    }
  }
}

function removeFiltering() {
  const { Driver, Names } = localStorage
  const { RoomsFiltering } = Names

  Driver.removeItem(RoomsFiltering.Options)
}

export default function* () {
  yield takeLatest(CalendarFiltersActions.ApplyFilters, applyFiltering)
  yield takeLatest(CalendarFiltersActions.CleanFilters, clearFiltering)
  yield takeLatest(CalendarFiltersActions.FilterByDate, centerScreen)
  yield takeLatest(actionTypes.CHANGE, updatedDaysCounter)
  yield takeLatest(actionTypes.CHANGE, updatedDateFrom)
  yield takeLatest(actionTypes.CHANGE, updatedDateTo)
  yield takeLatest(AuthorizationActions.SignOffTheAccount, removeFiltering)
}

interface IUpdateDaysFieldAction extends FormAction {
  meta?: {
    form: string
    field: string
  }
  payload: number
}

interface IUpdateDateToAction extends FormAction {
  meta?: {
    form: string
    field: string
  }
  payload: Date
}
